import { AssetSpec, SpineAssetSpec, SpriteAssetSpec, TextAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { Point } from "@westpierstudio/luma/layout/DefaultLayout";
import { GameServices } from "game/GameServices";
import { Container, Sprite, Text, TextMetrics, TextStyle, Texture } from "pixi.js";
import { Spine } from "pixi-spine";

export type TextStyleSpec = TextAssetSpec["style"]
type TokenCustom = { customBounds?: TextBounds }
type TokenSpec = (TextStyleSpec | SpriteAssetSpec | SpineAssetSpec) & TokenCustom
type TextTokenStylePair = { token: string, spec: TextStyleSpec }

export type TextFactoryTokens = Record<string, TokenSpec>

type ParseOutput = ParseText | ParseToken | ParseSpace | ParseNewline
type ParseType = "token" | "text" | "space" | "newline"
type ParseToken = {
    type: "token"
    value: string
    customBounds?: Partial<TextBounds>
}
type ParseText = {
    type: "text"
    value: string
}
type ParseSpace = {
    type: "space"
    value: string
}
type ParseNewline = {
    type: "newline"
    value: string
}

type TextBounds = { width: number, height: number };
export type TextAlign = "left" | "right" | "center" | number
export type TextOptions = {
    wrap: boolean,
    wrapWidth: number,
    align: TextAlign,
    lineSpacing: number
    spriteCentering: "topOfLine" | "centerOnLine" | "onBaseline" | number
    anchor: number | Partial<Point> | undefined
    boundsScaling: "shrink" | "fit"
    bounds: Partial<TextBounds> | undefined
    lineCentering: "top" | "center" | "bottom" | number
    spaceWidthMultiplier: number
}
const defaultLineCenterMultiplier = {
    top: 0.25,
    center: 0.625,
    bottom: 1,
};
const defaultAlign: TextAlign = `left`;
const defaultTextOptions: TextOptions = {
    wrap: false,
    wrapWidth: Infinity,
    align: defaultAlign,
    lineSpacing: 0,
    spriteCentering: `onBaseline`,
    anchor: undefined,
    boundsScaling: `shrink`,
    bounds: undefined,
    lineCentering: `bottom`,
    spaceWidthMultiplier: 1
};

export type TextFactorySpec = AssetSpec & {
    messageKey: string
    options?: TextOptions
}

export class TextFactory {
    protected tokens: TextFactoryTokens;
    protected readonly createTextOptions: Partial<TextOptions>;

    protected textInstances: TextContainer[] = [];


    public get defaultStyle(): TextStyleSpec {
        if (typeof this.defaultTokenOrStyleSpec === `string`) {
            const defaultToken = this.tokens[this.defaultTokenOrStyleSpec];
            if (defaultToken && !(`_key` in defaultToken)) {
                return defaultToken;
            }
            else {
                return {};
            }
        }
        else {
            return this.defaultTokenOrStyleSpec ?? {};
        }
    }

    /**
     * New instance of TextFactory
     * @param services luma GameServices
     * @param defaultTokenOrStyleSpec Text style or string referencing a token in the "tokens" parameter
     * @param tokens provide an object with string key and Style/Sprite spec values which will be read and replaced when using createText
     * @param defaultCreateTextOptions provide default options which will be used when creating text using createText
     */
    constructor(public readonly services: GameServices, protected defaultTokenOrStyleSpec?: TextStyleSpec | string, tokens?: TextFactoryTokens, defaultCreateTextOptions?: Partial<TextOptions>) {
        this.tokens = tokens ?? {};

        this.tokens[`default`] = this.defaultStyle;
        this.createTextOptions = defaultCreateTextOptions ?? {};
    }

    /**
     * Creates text
     * @param text Raw text which will be parsed and displayed. insert a token inline with curly brackets
     * @example "Example text with default style, {green}Text which is using 'green' style, Sprite: {sprite}"
     * @param [parent] will add the new text object as a child if provided
     * @param [options] options which the text will use, overrides the default text options if provided
     * @param [spec] will apply a luma AssetSpec if provided
     * @returns The text object container
     */
    createText(text: string, parent?: Container, options?: Partial<TextOptions>, spec?: AssetSpec) {
        const { factory } = this.services;
        const parseOutput = this.parseTokens(text);
        const objects = this.createObjects(parseOutput);
        const container = new TextContainer(this, { ...this.createTextOptions, ...options });
        if (parent) {
            parent.addChild(container);
        }
        for (const iterator of objects) {
            container.add(iterator);
        }
        container.update();
        if (spec) {
            factory.applySpec(container, spec);
        }
        this.textInstances.push(container);
        return container;
    }

    /**
     * Creates text from a message key
     * @param messageKey retrieves the message using resources.getMessage
     * @param parent will add the new text object as a child if provided
     * @param [options] options which the text will use, overrides the default text options if provided
     * @param [spec] will apply a luma AssetSpec if provided
     * @returns The text object container
    */
    createTextFromMessageKey(messageKey: string, parent?: Container, options?: Partial<TextOptions>, spec?: AssetSpec) {
        let textObj;
        try {
            const text = this.services.resources.getMessage(messageKey);
            textObj = this.createText(text, parent, options, spec);
        } catch (error) {
            const errMsg = `Invalid messageKey: "${messageKey}"`;
            logWarn(errMsg, error);
            textObj = this.createText(errMsg, parent, options, spec);
        }
        textObj.name = messageKey;
        return textObj;
    }

    /**
     * Creates text from a spec
     * @param spec TextFactorySpec must include a messageKey, options provided will overwrite default text options
     * @param parent will add the new text object as a child if provided
     * @param additionalOptions options which the text will use, overrides the spec options and also the default text options if provided
     * @returns The text object container
    */
    createTextFromSpec(spec: TextFactorySpec, parent?: Container, additionalOptions?: Partial<TextOptions>) {
        const allOptions = { ...spec.options, ...additionalOptions };
        const textObj = this.createTextFromMessageKey(spec.messageKey, parent, allOptions, spec);
        return textObj;
    }

    parseTokens(text: string) {
        let textStart = 0, tokenStart;
        const tokenList: ParseOutput[] = [];
        const addTextToTokens = (text: string) => {
            const split = text.split(/([\r\n\t\f\v ])/).filter(n => n);
            if (split) {
                for (const substr of split) {
                    if (substr === ` `) {
                        tokenList.push({ type: `space`, value: substr });
                    }
                    else if (substr === `\n`) {
                        tokenList.push({ type: `newline`, value: substr });
                    }
                    else {
                        tokenList.push({ type: `text`, value: substr });
                    }
                }
            }
        };
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            if (char === `}` && tokenStart !== undefined) {
                tokenList.push({ type: `token`, value: text.slice(tokenStart, i) });
                textStart = i + 1;
                tokenStart = undefined;
            }
            else if (char === `{`) {
                if (textStart < i) {
                    addTextToTokens(text.slice(textStart, i));
                }
                tokenStart = i + 1;
                textStart = i;
            }
        }
        if (textStart !== undefined && textStart < text.length) {
            addTextToTokens(text.slice(textStart));
        }
        return tokenList;
    }

    createObjects(parseOutput: ParseOutput[]) {

        let currentStyle = { token: `default`, spec: this.defaultStyle };
        const createdObjects: ObjectOutput[] = [];
        for (const parse of parseOutput) {
            if (parse.type === `token`) {
                if (parse.value in this.tokens) {
                    const token = this.tokens[parse.value];
                    if (`customBounds` in token) {
                        parse.customBounds = token.customBounds;
                    }
                    if (`_key` in token) { //sprite OR spine
                        createdObjects.push(new ObjectSpriteSpine(this, parse, token));
                    }
                    else { //text style
                        currentStyle = { token: parse.value, spec: token };
                        createdObjects.push(new ObjectStyleMarker(this, parse));
                    }
                }
                else {
                    console.warn(`token "${parse.value}": token not found in TextFactory, continuing...`);
                }
            }
            else if (parse.type === `space`) {
                createdObjects.push(new ObjectSpace(this, parse, currentStyle));
            }
            else if (parse.type === `newline`) {
                createdObjects.push(new ObjectNewline(this, parse, currentStyle));
            }
            else if (parse.type === `text`) {
                createdObjects.push(new ObjectText(this, parse, currentStyle));
            }
        }
        return createdObjects;
    }

    getToken(k: string) {
        return this.tokens[k];
    }

    setToken(k: string, v: TokenSpec, update = true) {
        this.tokens[k] = v;
        if (k === this.defaultTokenOrStyleSpec) {
            this.tokens[`default`] = v;
        }
        if (update) {
            this.updateTokens();
        }
    }

    addTokens(newTokens: Record<string, TokenSpec>) {
        for (const [k, v] of Object.entries(newTokens)) {
            this.setToken(k, v, false);
        }
        this.updateTokens();
    }

    protected updateTokens() {
        for (const textContainer of this.textInstances) {
            textContainer.updateTokens(this.tokens);
        }
    }
}

export class TextContainer extends Container {

    options: TextOptions;
    protected inner = new Container();
    protected objectOutput: ObjectOutput[] = [];

    protected debugBoundSprite?: Sprite;

    public get text(): string {
        let str = ``;
        for (const obj of this.objectOutput) {
            if (obj.type === `token`) {
                str += `{${obj.value}}`;
            }
            else {
                str += obj.value;
            }
        }
        return str;
    }

    public set text(v: string) {
        this.reset();
        const parse = this.factory.parseTokens(v);
        const objects = this.factory.createObjects(parse);
        for (const obj of objects) {
            this.add(obj);
        }
        this.update();
    }


    constructor(protected readonly factory: TextFactory, options?: Partial<TextOptions>) {
        super();
        this.addChild(this.inner);
        this.options = { ...defaultTextOptions, ...options };
    }

    add(d: ObjectOutput, update: boolean = false) {
        if (`object` in d && d.object) {
            this.inner.addChild(d.object);
        }
        this.objectOutput.push(d);
        if (update) {
            this.update();
        }
    }

    reset() {
        for (const obj of this.objectOutput) {
            if (obj.object) {
                obj.object.destroy();
            }
        }
        this.objectOutput = [];
    }

    update() {
        let lineWidth = 0;
        let lineHeight = 0;
        let y = 0;
        let line: ObjectOutput[] = [];
        let lastSpaceOrTextObject: ObjectOutput;
        const { align, wrap, wrapWidth, lineSpacing, spriteCentering, lineCentering, spaceWidthMultiplier } = this.options;
        const newLine = () => {

            if (lastSpaceOrTextObject && lastSpaceOrTextObject.type === `space` && line.length > 0) { //if last object was a space remove it
                lineWidth -= (lastSpaceOrTextObject.width * spaceWidthMultiplier);
            }
            const centerSprite = (val: number) => {
                for (const prevLine of line) {
                    if (prevLine.object && prevLine instanceof ObjectSpriteSpine) {
                        prevLine.object.y += (lineHeight - prevLine.height) * val;
                    }
                }
            };
            if (typeof spriteCentering === `number`) {
                centerSprite(spriteCentering);
            }
            else switch (spriteCentering) {
                case `centerOnLine`:
                    centerSprite(0.5);
                    break;
                case `onBaseline`:
                    centerSprite(1);
                    break;
                default:
                    break;
            }
            const centerLine = (multiplier: number) => {
                for (const prevLine of line) {
                    const object = prevLine.object;
                    if (object && prevLine instanceof ObjectText) {
                        const textMetrics = prevLine.textMetrics as TextMetrics;
                        object.y += (lineHeight - textMetrics.fontProperties.ascent) * multiplier;
                    }
                }
            };
            if (typeof lineCentering === `string`) {
                centerLine(defaultLineCenterMultiplier[lineCentering] ?? 1);
            }
            else {
                centerLine(lineCentering);
            }
            const alignLine = (val: number) => {
                for (const prevLine of line) {
                    if (prevLine.object) {
                        prevLine.object.x -= lineWidth * val;
                    }
                }
            };
            if (typeof align === `number`) {
                alignLine(align);
            }
            else if (align === `center`) {
                alignLine(0.5);
            }
            else if (align === `right`) {
                alignLine(1);
            }
            for (const prevLine of line) {//stroke pushes text up
                if (prevLine instanceof ObjectText) {
                    const style = prevLine.object.style;
                    if (style.strokeThickness) {
                        prevLine.object.y -= style.strokeThickness / 2;
                        prevLine.object.x -= style.strokeThickness / 2;
                    }
                }
            }

            y += lineHeight + lineSpacing;
            line = [];
            lineWidth = 0;
            lineHeight = 0;
        };

        for (const obj of this.objectOutput) {
            if (obj.type === `space`) {
                const space = obj.width * spaceWidthMultiplier;
                if (line.length > 0) {//if space is first in a line do nothing
                    lineWidth += space;
                }
            }
            else if (obj.type === `newline`) {
                const newLineHeight = obj.height;
                lineHeight = Math.max(lineHeight, newLineHeight);
                newLine();
            }
            else if (`object` in obj && obj.object) {
                const object = obj.object;
                const childWidth = obj.width;
                const childHeight = obj.height;
                if (wrap && lineWidth + childWidth > wrapWidth) {//wrap
                    newLine();
                }
                lineHeight = Math.max(lineHeight, childHeight);
                object.y = y;
                object.x = lineWidth;
                lineWidth += childWidth;
                line.push(obj);
            }
            if (obj.type === `space` || obj.type === `text`) {
                lastSpaceOrTextObject = obj;
            }
        }
        newLine();


        this.updateBounds();
        this.updateAnchor(this.inner, align);
    }

    private updateBounds() {
        const { bounds, boundsScaling } = this.options;
        if (bounds) {
            this.inner.scale.x = this.inner.scale.y = boundsScaling === `fit` ? 10000 : 1;
            if (bounds.width) {
                if (this.inner.width > bounds.width) {
                    this.inner.width = bounds.width;
                }
            }
            if (bounds.height) {
                if (this.inner.height > bounds.height) {
                    this.inner.height = bounds.height;
                }
            }
            const minScale = Math.min(this.inner.scale.x, this.inner.scale.y);
            this.inner.scale.x = this.inner.scale.y = minScale;
        }
    }

    private updateAnchor(container: Container, align: TextAlign) {
        const { anchor } = this.options;
        if (anchor) {
            const alignFactor = typeof align === `number` ? align : align === `right` ? 1 : align === `center` ? 0.5 : 0;
            let anchorX, anchorY;
            if (typeof anchor === `number`) {
                anchorX = anchor - alignFactor;
                anchorY = anchor;
            }
            else {
                anchorX = (anchor.x ?? 0) - alignFactor;
                anchorY = (anchor.y ?? 0);
            }
            const containerScaleX = container.scale.x !== 0 ? container.scale.x : 1;
            const containerScaleY = container.scale.y !== 0 ? container.scale.y : 1;
            container.pivot.x = anchorX * container.width / containerScaleX;
            container.pivot.y = anchorY * container.height / containerScaleY;
        }
    }

    debugBounds() {
        const { bounds } = this.options;
        if (bounds) {
            if (!this.debugBoundSprite) {
                this.debugBoundSprite = new Sprite(Texture.WHITE);
                this.debugBoundSprite.alpha = 0.5;
                this.addChildAt(this.debugBoundSprite, 0);
            }
            this.updateAnchor(this.debugBoundSprite, `left`);
            if (bounds.width === undefined && bounds.height === undefined) {
                this.debugBoundSprite.width = 0;
                this.debugBoundSprite.height = 0;
            }
            else {
                this.debugBoundSprite.width = bounds.width ?? 100000;
                this.debugBoundSprite.height = bounds.height ?? 100000;
            }
        }
        else if (this.debugBoundSprite) {
            this.debugBoundSprite.width = 0;
            this.debugBoundSprite.height = 0;
        }
    }

    updateTokens(tokens: TextFactoryTokens) {
        for (const object of this.objectOutput) {
            if (`token` in object) {
                const textObject = object as ObjectTextBased;
                const newSpec = tokens[textObject.token.token];
                if (!(`_key` in newSpec)) {
                    textObject.token.spec = newSpec;
                    object.tokenUpdate();
                }
            }
        }
        this.update();
    }
}

type ObjectTextBased = ObjectText | ObjectSpace | ObjectNewline

abstract class ObjectOutput {

    object?: Container;

    public get width(): number {
        return 0;
    }

    public get height(): number {
        return 0;
    }

    public get type(): ParseType {
        return this.parsed.type;
    }

    public get value(): string {
        return this.parsed.value;
    }

    constructor(public textFactory: TextFactory, public parsed: ParseOutput) {

    }

    protected measureText(text: string, styleSpec: TextStyleSpec, noStrokeOrShadow = true) {
        const style = noStrokeOrShadow ? { ...styleSpec, strokeThickness: 0, dropShadowDistance: 0 } : { ...styleSpec };
        const measureStyle: TextStyle = new TextStyle(style);
        return TextMetrics.measureText(text, measureStyle);
    }

    protected measureTextCanvas(text: string, style: TextStyle) {
        const context = TextMetrics._context;
        context.font = style.toFontString();
        const textMetricsCanvas = context.measureText(text);
        return textMetricsCanvas;
    }

    abstract tokenUpdate(): void;
}

class ObjectText extends ObjectOutput {

    override object: Text;
    textMetrics: TextMetrics;

    public override get width(): number {
        return this.textMetrics.width;
    }

    public override get height(): number {
        return this.textMetrics.fontProperties.ascent;
    }

    constructor(public override textFactory: TextFactory, public override parsed: ParseText, public token: TextTokenStylePair) {
        super(textFactory, parsed);

        this.object = textFactory.services.factory.createText(parsed.value, { style: token.spec });
        this.object.name = parsed.value;
        this.textMetrics = this.measureText(parsed.value, token.spec);
    }

    tokenUpdate() {
        this.object.style = this.token.spec;
        this.object.updateText(false);
        this.textMetrics = this.measureText(this.parsed.value, this.token.spec);
    }
}

class ObjectSpace extends ObjectOutput {

    textMetrics: TextMetrics;

    public override get width(): number {
        return this.textMetrics.width;
    }

    public override get height(): number {
        return this.textMetrics.fontProperties.ascent;
    }

    constructor(public override textFactory: TextFactory, public override parsed: ParseSpace, public token: TextTokenStylePair) {
        super(textFactory, parsed);
        this.textMetrics = this.measureText(parsed.value, token.spec, false);
    }

    tokenUpdate() {
        this.textMetrics = this.measureText(this.parsed.value, this.token.spec, false);
    }
}

class ObjectNewline extends ObjectOutput {

    textMetrics: TextMetrics;

    public override get width(): number {
        return this.textMetrics.width;
    }

    public override get height(): number {
        return this.textMetrics.height;
    }

    constructor(public override textFactory: TextFactory, public override parsed: ParseNewline, public token: TextTokenStylePair) {
        super(textFactory, parsed);
        this.textMetrics = this.measureText(` `, token.spec);
    }

    tokenUpdate() {
        this.textMetrics = this.measureText(` `, this.token.spec);
    }
}

class ObjectSpriteSpine extends ObjectOutput {

    override object?: Sprite | Spine = undefined;
    customBounds?: Partial<TextBounds>;

    public override get width(): number {
        if (this.customBounds?.width !== undefined) return this.customBounds.width;
        else return this.object?.width ?? 0;
    }

    public override get height(): number {
        if (this.customBounds?.height !== undefined) return this.customBounds.height;
        else return this.object?.height ?? 0;
    }

    constructor(public override textFactory: TextFactory, public override parsed: ParseToken, public tokenSpec: SpriteAssetSpec | SpineAssetSpec) {
        super(textFactory, parsed);

        const { factory } = textFactory.services;

        if (`customBounds` in parsed) {
            this.customBounds = parsed.customBounds;
        }

        try {
            const sprite = factory.createSprite(tokenSpec);
            sprite.name = parsed.value;
            this.object = sprite;
        } catch (errorSprite) {
            try {
                const spine = factory.createSpine(tokenSpec);
                spine.name = parsed.value;
                this.object = spine;
            }
            catch (errorSpine) {
                logWarn(`token "${parsed.value}": could not be created as a spine or sprite`);
                logWarn(errorSprite);
                logWarn(errorSpine);
            }
        }
    }

    tokenUpdate() {

    }
}

class ObjectStyleMarker extends ObjectOutput {

    constructor(public override textFactory: TextFactory, public override parsed: ParseOutput) {
        super(textFactory, parsed);
    }

    tokenUpdate(): void {

    }
}

export function log(message?: any, ...optionalParams: any[]): void {

    if (process.env.NODE_ENV !== `production`) {
        console.log(message, ...optionalParams);
    }
}

export function logWarn(message?: any, ...optionalParams: any[]): void {

    if (process.env.NODE_ENV !== `production`) {
        console.warn(message, ...optionalParams);
    }
}
